import type React from "react";
import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import type { Role, Permission, RolePermission } from "./types/permissions";
import { permissions } from "./mockData/data";
import AddPermissionModal from "./AddPermission";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";
import CustomTextField from "../../../components/common/Textfield";

interface PermissionsManagementProps {
  role: Role;
  onBack: () => void;
  onUpdatePermissions: (roleId: string, permissions: RolePermission[]) => void;
}

const PermissionsManagement: React.FC<PermissionsManagementProps> = ({
  role,
  onBack,
  onUpdatePermissions,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>(
    role.permissions
  );
  const [addPermissionModalOpen, setAddPermissionModalOpen] = useState(false);
  const [availablePermissions, setAvailablePermissions] =
    useState<Permission[]>(permissions);

  const filteredPermissions = availablePermissions.filter(
    (permission) =>
      permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRolePermission = (
    permissionId: string
  ): RolePermission | undefined => {
    return rolePermissions.find((rp) => rp.permissionId === permissionId);
  };

  const updatePermission = (
    permissionId: string,
    field: keyof RolePermission,
    value: boolean
  ) => {
    setRolePermissions((prev) => {
      const existing = prev.find((rp) => rp.permissionId === permissionId);
      if (existing) {
        return prev.map((rp) =>
          rp.permissionId === permissionId ? { ...rp, [field]: value } : rp
        );
      } else {
        const newPermission: RolePermission = {
          roleId: role.id,
          permissionId,
          canCreate: field === "canCreate" ? value : false,
          canRead: field === "canRead" ? value : false,
          canUpdate: field === "canUpdate" ? value : false,
          canDelete: field === "canDelete" ? value : false,
        };
        return [...prev, newPermission];
      }
    });
  };

  const handleSavePermissions = () => {
    onUpdatePermissions(role.id, rolePermissions);
  };

  const handleAddPermission = (newPermission: {
    name: string;
    description: string;
    category: string;
  }) => {
    const permission: Permission = {
      id: Date.now().toString(),
      name: newPermission.name,
      description: newPermission.description,
      category: newPermission.category,
    };
    setAvailablePermissions((prev) => [...prev, permission]);
    setAddPermissionModalOpen(false);
  };

  const hasAnyPermission = (permissionId: string): boolean => {
    const rolePermission = getRolePermission(permissionId);
    return rolePermission
      ? rolePermission.canCreate ||
          rolePermission.canRead ||
          rolePermission.canUpdate ||
          rolePermission.canDelete
      : false;
  };

  const MotionTableRow = motion(TableRow);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        <Paper sx={{ boxShadow: 0, p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton onClick={onBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box flexGrow={1}>
              <Typography variant="h5" fontWeight="bold">
                {role.title} - Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage permissions for {role.title} role
              </Typography>
            </Box>

            <Button
              onClick={handleSavePermissions}
              sx={{
                backgroundColor: reduxTheme.primary_colors[400],
                color: reduxTheme.primary_colors[900],
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Paper>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <CustomTextField
            placeholder="Search permissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, maxWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddPermissionModalOpen(true)}
              sx={{ mr: 1 }}
            >
              Add Permission
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{ backgroundColor: reduxTheme.secondary_colors[500] }}
              >
                <TableCell size="small">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Permission Name
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Create
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Read
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Update
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Delete
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPermissions.map((permission) => {
                const rolePermission = getRolePermission(permission.id);
                return (
                  <MotionTableRow
                    key={permission.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    sx={{
                      "&:hover": { backgroundColor: "grey.50" },
                      height: "auto",
                      backgroundColor: hasAnyPermission(permission.id)
                        ? "primary.50"
                        : "inherit",
                    }}
                  >
                    <TableCell size="small" sx={{ py: 0.5, px: 1 }}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {permission.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {permission.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={rolePermission?.canCreate || false}
                        onChange={(e) =>
                          updatePermission(
                            permission.id,
                            "canCreate",
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={rolePermission?.canRead || false}
                        onChange={(e) =>
                          updatePermission(
                            permission.id,
                            "canRead",
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={rolePermission?.canUpdate || false}
                        onChange={(e) =>
                          updatePermission(
                            permission.id,
                            "canUpdate",
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={rolePermission?.canDelete || false}
                        onChange={(e) =>
                          updatePermission(
                            permission.id,
                            "canDelete",
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete Permission">
                        <IconButton size="small" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </MotionTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <AddPermissionModal
          open={addPermissionModalOpen}
          onClose={() => setAddPermissionModalOpen(false)}
          onSave={handleAddPermission}
        />
      </Box>
    </motion.div>
  );
};

export default PermissionsManagement;
